import {
    Alert,
    Divider,
    List,
    ListItem,
    ListItemButton,
    Stack,
    Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { Dispatch, FC, SetStateAction } from 'react';

import {
    CalendarEvent,
    InterviewBlock,
    UserEvent,
} from '@features/enterpriseEvents';
import {
    borderGrey,
    darkBlue,
    darkBlue2,
    lightGrey,
    white,
} from '@styles/colors';
import { bold, light, medium } from '@styles/fontWeights';

type UpcomingInterviewsProps = {
    upcomingInterviews: CalendarEvent[];
    setSingleBlockInterview: Dispatch<
        SetStateAction<InterviewBlock | UserEvent | null>
    >;
};

const UpcomingInterviews: FC<UpcomingInterviewsProps> = ({
    upcomingInterviews,
    setSingleBlockInterview,
}) => {
    const currentDateTime = new Date();
    return (
        <>
            <Typography variant="h3">Upcoming Interviews</Typography>
            <Divider />
            {upcomingInterviews?.filter(
                (event) =>
                    new Date(event.block?.dt_start_time ?? event?.dt_start_time) >= currentDateTime && event.s_status === 2,
            ).length ? (
                <List
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        maxHeight: '100vh',
                        overflowY: 'auto',
                    }}
                >
                    {upcomingInterviews
                        .filter((item) => item.title !== 'Open Interviews' && new Date(item.block?.dt_start_time ?? item?.dt_start_time) >= currentDateTime && item.s_status === 2)
                        .map((event) => (
                            <ListItem key={event.id} sx={{ p: 0 }}>
                                <ListItemButton
                                    sx={{
                                        justifyContent: 'space-between',
                                        alignItems: { xs: 'flex-start', xl: 'center' },
                                        flexDirection: { sm: 'column', md: 'row' },
                                        borderBottom: `1px solid ${borderGrey}`,
                                        px: 0,
                                    }}
                                    onClick={() => {
                                        setSingleBlockInterview(event);
                                    }}
                                >
                                    <Stack
                                        sx={{ m: 1 }}
                                        spacing={3}
                                        direction="row"
                                        alignItems="center"
                                    >
                                        <Stack spacing={0} alignItems="center">
                                            {event.slot ? (
                                                <>
                                                    <Typography
                                                        variant="h2"
                                                        component="p"
                                                        fontWeight={medium}
                                                    >
                                                        {dayjs
                                                            (event.slot?.slot_start_time)
                                                            .format('DD')}
                                                    </Typography>
                                                    <Typography
                                                        variant="h4"
                                                        component="p"
                                                        fontWeight={light}
                                                    >
                                                        {dayjs(event.slot?.slot_start_time)
                                                            .format('MMM')}
                                                    </Typography>
                                                </>
                                            ) : (
                                                <>
                                                    <Typography
                                                        variant="h2"
                                                        component="p"
                                                        fontWeight={medium}
                                                    >
                                                        {dayjs(
                                                                event.block?.dt_start_time ??
                                                                event?.dt_start_time,
                                                            )
                                                            .format('DD')}
                                                    </Typography>
                                                    <Typography
                                                        variant="h4"
                                                        component="p"
                                                        fontWeight={light}
                                                    >
                                                        {dayjs(
                                                                event.block?.dt_start_time ??
                                                                event?.dt_start_time,
                                                            )
                                                            .format('MMM')}
                                                    </Typography>
                                                </>
                                            )}
                                        </Stack>
                                        <Stack
                                            sx={{
                                                marginLeft: '10px !important',
                                            }}
                                        >
                                            <Typography
                                                component="span"
                                                variant="body1"
                                                sx={{
                                                    display: 'inline-block',
                                                    fontSize: '0.7rem',
                                                    backgroundColor: lightGrey,
                                                    color: darkBlue,
                                                    padding: '0.15rem .5rem',
                                                    marginBottom: '7px !important',
                                                    borderRadius: 5,
                                                    textAlign: 'center',
                                                    width: 'fit-content',
                                                }}
                                            >
                                                {event.company.s_name}
                                            </Typography>
                                            <Typography fontWeight={bold} sx={{ fontSize: '0.9rem' }}>
                                                {' '}
                                                {event.application.applicant.name}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.9rem' }}>
                                                {' '}
                                                {event.application.position}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                    <Stack
                                        sx={{ m: 1 }}
                                        spacing={3}
                                        direction="row"
                                        alignItems="center"
                                    >
                                        <Typography
                                            component="span"
                                            variant="body1"
                                            sx={{
                                                display: 'inline-block',
                                                fontSize: '0.8rem',
                                                backgroundColor: lightGrey,
                                                color: darkBlue,
                                                padding: '0.5rem .4rem',
                                                marginBottom: '5px !important',
                                                borderRadius: 5,
                                                alignSelf: 'right',
                                            }}
                                        >
                                            {event.slot
                                                ? `${dayjs(event?.slot?.slot_start_time).format('hh:mm')} - ${dayjs(event?.slot?.slot_end_time).format('hh:mm a')}`
                                                : `${dayjs(event?.block?.dt_start_time ?? event?.dt_start_time).format('hh:mm')} - ${dayjs(event?.block?.dt_end_time ?? event?.dt_end_time).format('hh:mm a')}`}
                                        </Typography>
                                    </Stack>
                                </ListItemButton>
                            </ListItem>
                        ))}
                </List>
            ) : (
                <Alert severity="info">You have no upcoming Interviews.</Alert>
            )}
        </>
    );
};

export default UpcomingInterviews;
